import { useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

type LenisInstance = {
  raf: (time: number) => void;
  destroy: () => void;
  on: (event: string, cb: () => void) => void;
};

const shouldReduceMotion = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(prefers-reduced-motion: reduce)").matches || window.innerWidth < 768);

export const useSmoothScroll = () => {
  useEffect(() => {
    if (typeof window === "undefined" || shouldReduceMotion()) {
      return;
    }

    let lenis: LenisInstance | null = null;
    let active = true;

    const tick = (time: number) => {
      // Lenis expects milliseconds
      lenis?.raf(time * 1000);
    };

    const setup = async () => {
      const { default: Lenis } = await import("lenis");
      if (!active) {
        return;
      }

      lenis = new Lenis({
        lerp: 0.08, // Slightly smoother
        wheelMultiplier: 0.85,
        touchMultiplier: 1.0,
      }) as unknown as LenisInstance;

      // Update ScrollTrigger when Lenis scrolls
      lenis.on("scroll", () => {
        ScrollTrigger.update();
      });

      // Drive Lenis scroll tick through GSAP's ticker loop
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(0);

      // Refresh ScrollTrigger to recalculate layout offsets once Lenis and DOM are ready
      setTimeout(() => {
        ScrollTrigger.refresh();
      }, 100);
    };

    void setup();

    return () => {
      active = false;
      gsap.ticker.remove(tick);
      lenis?.destroy();
    };
  }, []);
};

